import React from "react";
import { Icon, Checkbox, Popup } from "semantic-ui-react";
import OntologyClassesTable from "./OntologyClassesTable";
import { getAllVariablesFor, sortNodes } from "../utils";

// Return an array of path keys for a node and all its descendant nodes.
const getChildrenKeys = (node) => {
  const collect = (n) => [n.pathKey ?? n.key, ...(n.children || []).flatMap(collect)];
  return collect(node);
};

function getNodeState(node, selectedColumns) {
  const hasVariables = node.variables.length > 0;

  const allVariablesSelected =
    hasVariables &&
    node.variables.every((variable) => selectedColumns.includes(variable));

  const hasChildren = node.children.length > 0;

  if (!hasVariables && !hasChildren) {
    throw new Error(`Node '${node.key}' has no variables nor children`);
  }

  if (allVariablesSelected && !hasChildren) {
    return "selected";
  }

  const childrenStates = hasChildren ? node.children.map(n => getNodeState(n, selectedColumns)) : [];

  const allChildrenSelected =
    hasChildren && childrenStates.every((state) => state === "selected");

  if (allChildrenSelected && (allVariablesSelected || !hasVariables)) {
    return "selected";
  }

  const someVariablesSelected =
    hasVariables &&
    node.variables.some((variable) => selectedColumns.includes(variable));

  const someChildrenSelected =
    allChildrenSelected ||
    (hasChildren &&
      childrenStates.some(
        (state) => state === "selected" || state === "indeterminate"
      ));

  if (someVariablesSelected || someChildrenSelected) {
    return "indeterminate";
  }

  return "unselected";
};

const ColumnSelectionNode = ({
  node,
  isExpanded: isExpandedFn = () => false,
  userExpanded = {},
  setExpanded,
  getParentKeys = () => [],
  selectedColumns = [],
  onToggle,
  highlightText = "",
}) => {
  const hasChildren = node.children?.length > 0;
  const expandKey = node.pathKey ?? node.key;
  const isExpanded = isExpandedFn(expandKey);
  const isUserExpanded = userExpanded[expandKey];
  const nodeState = getNodeState(node, selectedColumns);

  // Helper to highlight matching text
  const highlightMatch = (text) => {
    if (!highlightText.trim() || !text) return text;

    // Escape special regex characters in highlightText so they are treated literally
    const regex = new RegExp(`(${highlightText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, i) =>
      regex.test(part) ? (
        <span key={i} className="highlight-match">{part}</span>
      ) : part
    );
  };

  // Batch-set expansion for a set of keys. `expand` coerced to boolean.
  const setExpandedForKeys = (keys, expand) => {
    const value = !!expand;
    setExpanded((prev) => {
      const copy = { ...prev };
      keys.forEach((k) => {
        copy[k] = value;
      });
      return copy;
    });
  };

  // When selecting a node while filtered, expand its descendants and
  // and pass the set of touched keys to `onToggle` so the parent component
  // can persist them.
  const toggleNodeVariables = (node) => {
    const selected = nodeState !== "selected";
    const allVariablesForNode = getAllVariablesFor(node);

    let touchedKeys = [];
    if (selected && !isUserExpanded) {
      const childrenKeys = getChildrenKeys(node);
      const parentKeys = getParentKeys(expandKey) || [];
      const keysToSet = Array.from(new Set([...childrenKeys, ...parentKeys]));

      setExpandedForKeys(keysToSet, true);

      touchedKeys = keysToSet;
    }

    onToggle(allVariablesForNode, selected, touchedKeys);
  };



  const checkboxName = node.key.replace(/[^a-zA-Z0-9_-]/g, "_");

  const nodeContent = (
    <button
      className="button-reset node-button"
      onClick={() => setExpandedForKeys([expandKey], !userExpanded[expandKey])}
      aria-label={`${hasChildren ? "Toggle selection of" : "Select"} ${node.label
        }`}
    >
      <Checkbox
        className="node-checkbox"
        indeterminate={nodeState === "indeterminate"}
        checked={nodeState === "selected"}
        onChange={(e) => {
          e.stopPropagation();
          toggleNodeVariables(node);
        }}
        name={checkboxName}
      />
      <label
        htmlFor={checkboxName}
        className="node-label"
        style={{ fontWeight: hasChildren ? "bold" : "normal" }}
      >
        {highlightMatch(node.label)}
      </label>
    </button>
  );

  return (
    <div className="node-container" key={node.key}>
      <div className="node-button-container">
        {hasChildren && (
          <button
            onClick={() => setExpandedForKeys([expandKey], !userExpanded[expandKey])}
            aria-expanded={isExpanded}
            aria-haspopup={node.classes.length > 0}
            aria-label={`${isExpanded ? "Collapse" : "Expand"} ${node.label}`}
            className="button-reset chevron-button"
          >
            <Icon name={isExpanded ? "chevron down" : "chevron right"} />
          </button>
        )}
        {node.classes.length > 0 ? (
          <Popup
            trigger={nodeContent}
            content={<OntologyClassesTable classes={node.classes} />}
            on="hover"
            position="right center"
            wide="very"
          />
        ) : (
          nodeContent
        )}
      </div>
      {hasChildren && isExpanded && (
        <div className="children-container">
          {node.children.toSorted(sortNodes).map((child) => (
            <ColumnSelectionNode
              key={child.key}
              node={child}
              isExpanded={isExpandedFn}
              userExpanded={userExpanded}
              setExpanded={setExpanded}
              getParentKeys={getParentKeys}
              selectedColumns={selectedColumns}
              onToggle={onToggle}
              highlightText={highlightText}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ColumnSelectionNode;
